import { ErrorAlert } from "#components/ErrorAlert"
import { useCanPerformMutations } from "#hooks/use-can-perform-mutations"
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "#hooks/use-categories"
import { useI18n } from "#hooks/use-i18n"
import { slideUp } from "#lib/animations"
import { getUserFriendlyErrorMessage } from "#lib/errors"
import { createTranslatedCategorySchemas } from "#lib/translated-schemas"
import type {
  CreateExpenseCategory,
  ExpenseCategory,
  UpdateExpenseCategory,
} from "@home-finance-manager/shared/types/expense_categories"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table"
import { motion } from "framer-motion"
import { Pencil, Trash2 } from "lucide-react"
import React, { useCallback, useMemo, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useSearchParams } from "react-router-dom"
import type { z } from "zod"
import { CategoriesHeaderActions } from "../components/categories/CategoriesHeaderActions"
import { Dialog, DialogActions } from "../components/Dialog"
import { PageHeader } from "../components/page-header"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import { Tooltip, TooltipContent, TooltipTrigger } from "../components/ui/tooltip"
import { Body } from "../components/ui/typography"
import { useAccount } from "../contexts/AccountContext"
import { getCategoryBackgroundStyle } from "../lib/category-background"

type CategoryFormData =
  | z.input<ReturnType<typeof createTranslatedCategorySchemas>["createCategorySchema"]>
  | z.input<ReturnType<typeof createTranslatedCategorySchemas>["updateCategorySchema"]>

const Categories: React.FC = () => {
  const { t } = useI18n()
  const { currentAccount } = useAccount()
  const canPerformMutations = useCanPerformMutations()
  const { data: categories = [], isLoading: loading } = useCategories()
  const createCategory = useCreateCategory()
  const updateCategory = useUpdateCategory()
  const deleteCategory = useDeleteCategory()
  const [showAddCategory, setShowAddCategory] = useState(false)
  const [editingCategory, setEditingCategory] = useState<ExpenseCategory | null>(null)
  const [deletingCategory, setDeletingCategory] = useState<ExpenseCategory | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  // Get sorting from URL
  const sorting = useMemo<SortingState>(() => {
    const sortParam = searchParams.get("sort")
    if (!sortParam) return []
    try {
      const parsed = JSON.parse(sortParam)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }, [searchParams])

  const setSorting = useCallback(
    (newSorting: SortingState | ((prev: SortingState) => SortingState)) => {
      setSearchParams((prev) => {
        const updatedSorting = typeof newSorting === "function" ? newSorting(sorting) : newSorting
        const newParams = new URLSearchParams(prev)
        if (updatedSorting.length === 0) {
          newParams.delete("sort")
        } else {
          newParams.set("sort", JSON.stringify(updatedSorting))
        }
        return newParams
      })
    },
    [setSearchParams, sorting]
  )

  const { createCategorySchema, updateCategorySchema } = useMemo(
    () => createTranslatedCategorySchemas(t),
    [t]
  )
  const currentSchema = useMemo(
    () => (editingCategory ? updateCategorySchema : createCategorySchema),
    [editingCategory, createCategorySchema, updateCategorySchema]
  )

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CategoryFormData>({
    resolver: zodResolver(currentSchema),
    defaultValues: {
      name: "",
      description: "",
      color: "",
    },
  })

  const predefinedColors = [
    "#D4AF37",
    "#10B981",
    "#F59E0B",
    "#3B82F6",
    "#8B5CF6",
    "#EF4444",
    "#06B6D4",
    "#84CC16",
    "#F97316",
    "#EC4899",
    "#6366F1",
    "#14B8A6",
  ]

  const onSubmit = async (data: CategoryFormData) => {
    if (!currentAccount) return

    setError(null)

    try {
      if (editingCategory) {
        const updateData: UpdateExpenseCategory = {
          ...(data.name && { name: data.name }),
          description: data.description ?? "",
          ...(data.color && data.color.trim() !== "" && { color: data.color }),
        }
        await updateCategory.mutateAsync({
          id: editingCategory.id,
          data: updateData,
        })
      } else {
        const createData: CreateExpenseCategory = {
          name: data.name as string,
          description: (data.description ?? "") as string,
          ...(data.color && data.color.trim() !== "" && { color: data.color }),
        }
        await createCategory.mutateAsync(createData)
      }
      handleCloseDialog()
    } catch (err) {
      setError(getUserFriendlyErrorMessage(err))
    }
  }

  const handleEditCategory = (category: ExpenseCategory) => {
    setEditingCategory(category)
    reset({
      name: category.name,
      description: category.description || "",
      color: category.color,
    })
    setShowAddCategory(true)
  }

  const handleDeleteCategory = async () => {
    if (!deletingCategory) return

    try {
      setError(null)
      await deleteCategory.mutateAsync(deletingCategory.id)
      setDeletingCategory(null)
    } catch (err) {
      setError(getUserFriendlyErrorMessage(err))
      setDeletingCategory(null)
    }
  }

  const handleCloseDialog = () => {
    setShowAddCategory(false)
    setEditingCategory(null)
    setError(null)
    reset({
      name: "",
      description: "",
      color: "",
    })
  }

  const columns = useMemo<ColumnDef<ExpenseCategory>[]>(
    () => [
      {
        accessorKey: "color",
        header: t("pages.categories.table.color"),
        cell: ({ row }) => {
          const backgroundStyle = getCategoryBackgroundStyle(row.original.color, row.original.id)
          return <div className="w-10 h-10 rounded-lg flex-shrink-0" style={backgroundStyle} />
        },
      },
      {
        accessorKey: "name",
        header: t("pages.categories.table.name"),
        cell: ({ row }) => <div className="font-medium">{row.original.name}</div>,
      },
      {
        accessorKey: "description",
        header: t("pages.categories.table.description"),
        cell: ({ row }) => (
          <div className="text-sm text-muted-foreground">{row.original.description || "-"}</div>
        ),
      },
      {
        id: "actions",
        header: t("pages.categories.table.actions"),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {canPerformMutations ? (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleEditCategory(row.original)}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="destructive"
                  size="icon"
                  onClick={() => setDeletingCategory(row.original)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button variant="outline" size="icon" disabled>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("pages.categories.tooltips.editDisabled")}</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button variant="destructive" size="icon" disabled>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("pages.categories.tooltips.deleteDisabled")}</p>
                  </TooltipContent>
                </Tooltip>
              </>
            )}
          </div>
        ),
      },
    ],
    [canPerformMutations, t]
  )

  const table = useReactTable({
    data: categories,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  })

  if (loading) {
    return (
      <div className="min-h-screen text-primary p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <Body className="text-secondary-foreground">{t("pages.categories.loading")}</Body>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 flex flex-col">
      <PageHeader
        title={t("pages.categories.pageTitle")}
        subtitle={t("pages.categories.pageSubtitle")}
        actions={
          <CategoriesHeaderActions
            isOwner={canPerformMutations}
            onCreateCategory={() => {
              setEditingCategory(null)
              reset({ name: "", description: "", color: "" })
              setShowAddCategory(true)
            }}
          />
        }
      />

      {/* Error Message */}
      <ErrorAlert error={error} onDismiss={() => setError(null)} />

      {/* Categories Table */}
      <motion.div initial="hidden" animate="visible" variants={slideUp} className="flex-1 min-h-0">
        <Card className="flex-1 min-h-0 flex flex-col">
          <CardHeader className="flex-shrink-0">
            <CardTitle>{t("pages.categories.tableTitle")}</CardTitle>
            <CardDescription>{t("pages.categories.tableDescription")}</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 overflow-auto">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="h-24 text-center">
                        {t("pages.categories.noCategories")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Add/Edit Category Dialog */}
      <Dialog
        open={showAddCategory}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseDialog()
          }
        }}
        title={
          editingCategory ? t("pages.categories.editTitle") : t("pages.categories.createTitle")
        }
        description={
          editingCategory
            ? t("pages.categories.editDescription")
            : t("pages.categories.createDescription")
        }
        size="md"
        footer={
          <DialogActions
            onCancel={handleCloseDialog}
            onConfirm={handleSubmit(onSubmit)}
            confirmText={
              editingCategory
                ? t("pages.categories.form.update")
                : t("pages.categories.form.create")
            }
            confirmVariant="default"
            cancelText={t("components.dialog.cancel")}
            isLoading={createCategory.isPending || updateCategory.isPending}
            isConfirmDisabled={!canPerformMutations}
            confirmTooltip={
              !canPerformMutations ? t("pages.categories.tooltips.actionDisabled") : undefined
            }
          />
        }
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <ErrorAlert error={error} onDismiss={() => setError(null)} />

          <div className="space-y-3">
            <Label htmlFor="name">{t("pages.categories.form.name")}</Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  id="name"
                  type="text"
                  placeholder={t("pages.categories.form.namePlaceholder")}
                  {...field}
                  className={errors.name ? "border-red-500" : ""}
                />
              )}
            />
            {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
          </div>

          <div className="space-y-3">
            <Label htmlFor="description">{t("pages.categories.form.description")}</Label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Input
                  id="description"
                  type="text"
                  placeholder={t("pages.categories.form.descriptionPlaceholder")}
                  {...field}
                  value={field.value ?? ""}
                  className={errors.description ? "border-red-500" : ""}
                />
              )}
            />
            {errors.description && (
              <p className="text-sm text-red-500">{errors.description.message}</p>
            )}
          </div>

          <div className="space-y-3">
            <Label>
              {t("pages.categories.form.color")}{" "}
              <span className="text-muted-foreground text-sm">
                {t("pages.categories.form.colorOptional")}
              </span>
            </Label>
            <p className="text-sm text-muted-foreground">
              {t("pages.categories.form.colorDescription")}
            </p>
            <div className="grid grid-cols-6 gap-3 mb-3">
              {predefinedColors.map((color) => (
                <Controller
                  key={color}
                  name="color"
                  control={control}
                  render={({ field }) => (
                    <button
                      type="button"
                      onClick={() => field.onChange(color)}
                      className={`w-10 h-10 rounded-xl border-2 transition-all duration-200 ${
                        field.value === color
                          ? "border-foreground ring-2 ring-primary scale-110"
                          : "border-border hover:border-foreground/50"
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  )}
                />
              ))}
            </div>
            <Controller
              name="color"
              control={control}
              render={({ field }) => (
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={field.value || "#3B82F6"}
                    onChange={(e) => field.onChange(e.target.value)}
                    className="w-full h-12 cursor-pointer"
                  />
                  {field.value && (
                    <Button type="button" variant="outline" onClick={() => field.onChange("")}>
                      {t("pages.categories.form.clear")}
                    </Button>
                  )}
                </div>
              )}
            />
            {errors.color && <p className="text-sm text-red-500">{errors.color.message}</p>}
          </div>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deletingCategory}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingCategory(null)
          }
        }}
        title={t("pages.categories.deleteTitle")}
        description={t("pages.categories.deleteDescription")}
        size="sm"
        footer={
          <DialogActions
            onCancel={() => setDeletingCategory(null)}
            onConfirm={handleDeleteCategory}
            confirmText={t("components.dialog.delete")}
            cancelText={t("components.dialog.cancel")}
            confirmVariant="destructive"
            isLoading={deleteCategory.isPending}
            isConfirmDisabled={!canPerformMutations}
            confirmTooltip={
              !canPerformMutations
                ? "Only account owners and admins can perform this action"
                : undefined
            }
          />
        }
      >
        <div className="space-y-3">
          <Body className="text-foreground">
            {t("pages.categories.deleteWarning", { name: deletingCategory?.name })}
          </Body>
          <Body className="text-muted-foreground text-sm">{t("pages.categories.deleteNote")}</Body>
        </div>
      </Dialog>
    </div>
  )
}

export default Categories
